import * as React from "react";
import {navigate, RouteComponentProps} from "@reach/router";
import UserStore from "../stores/UserStore";
import {inject} from "mobx-react";
import NotFoundPage from "../pages/NotFoundPage";
import SignInPage from "../pages/signIn/SignInPage";
import LoadingPage from "../pages/LoadingPage";
import Tracking from "./Tracking";
import config from "react-global-configuration";

export const Routes = {
  index: "/index.html",
  account: "/account",
  accountEdit: "/accountEdit",
  accounts: "/accounts",
  accountsEdit: "/accountsEdit",
  class: "/class",
  accountClassEdit: "/accountClassEdit",
  courseEdit: "/courseEdit",
  courses: "/courses",
  home: "/home", // Must be last
  myClasses: "/myclasses",
  notFound: "/404",
  people: "/people",
  register: "/register",
  reports: "/reports",
  signout: "/signout",
  signup: "/signup",
  signin: "/",
}

/**
 * undefined -> allows unauthenticated access
 * isAuthenticated -> requires authentication
 * isOwner -> requires owner role
 * isAdmin -> requires admin role
 */
const RouteRequirements = {
  account: {isAuthenticated: true},
  accountEdit: {isAuthenticated: true, isOwner: true},
  accounts: {isAuthenticated: true, isAdmin: true},
  // TODO: See if accountsEdit is used
  accountsEdit: {isAuthenticated: true, isOwner: true},
  accountClassEdit: {isAuthenticated: true, isOwner: true},
  class: {isAuthenticated: true},
  courses: {isAuthenticated: true, isAdmin: true},
  courseEdit: {isAuthenticated: true, isAdmin: true},
  myClasses: {isAuthenticated: true},
  people: {isAuthenticated: true, isAdmin: true},
  reports: {isAuthenticated: true, isOwner: true}
}

interface IControlTowerProps {
  userStore?: UserStore
  children?: any
}

@inject("userStore")
class ControlTower extends React.Component<IControlTowerProps & RouteComponentProps> {

  private lastNavigatedRoute = "";

  static route = (to: string, options?: any) => {
    if (to.startsWith("http")) {
      window.location.href = to
    } else if (to === Routes.home) {
      let homeUrl = config.get('homeUrl')
      if (homeUrl) {
        window.location.href = homeUrl
      } else {
        navigate(Routes.signin, options)
      }
  } else {
      navigate(to, options)
    }
  }

  static back = () => {
    window.history.back()
  }

  static open = (to: string, target: string = "_blank") => {
    window.open(to, target)
  }

  static get currentRoute(): string {
    return window.location.pathname
  }

  render() {
    const route = "/" + this.props["*"]

    const {userStore} = this.props

   if (userStore && userStore!.isLoading) {
      console.log(`Routing ${route} to LoadingPage`)
      return <LoadingPage userStore={userStore} route={route}/>
    } else if (userStore && !userStore.isAuthenticated && this.routeRequiresLogin(route)) {
      console.log(`Routing ${route} to SignInPage for authentication`)
      return <SignInPage route={route}/>
    } else if (userStore && this.routeRequiresAdmin(route) && !userStore.isAdmin) {
      console.log(`Route ${route} requires admin`)
      return <NotFoundPage/>
   } else if (userStore && this.routeRequiresOwner(route) && !(userStore.isOwner || userStore.isAdmin)) {
     console.log(`Route ${route} requires owner`)
     return <NotFoundPage/>
    } else if (this.routeExists(route)) {
      console.log(`Routing to ${route}`)
      this.lastNavigatedRoute = route;
      Tracking.pageView(route)
      return (<React.Fragment>{this.props.children}</React.Fragment>)
    } else {
      console.log(`Route ${route} not found`)
      this.lastNavigatedRoute = Routes.notFound;
      return <NotFoundPage/>
    }
  }

  private routeExists= (route: string): boolean => {
    const routeKey = Object.keys(Routes).find(key => route.startsWith(Routes[key]))
    if (routeKey) {
      return true
    }

    return false
  }

  private routeRequiresLogin = (route: string): boolean => {
    // Check if the route has any requirements
    const routeKey = Object.keys(Routes).find(key => route.startsWith(Routes[key]))
    if (routeKey) {
      const routeRequirements = RouteRequirements[routeKey]
      if (routeRequirements) {
        // console.log(`${route} requires login`)
        return true
      }
    }

    return false
  }

  private routeRequiresAdmin = (route: string): boolean => {
    // Check if the route has any requirements
    const routeKey = Object.keys(Routes).find(key => route.startsWith(Routes[key]))
    if (routeKey) {
      const routeRequirements = RouteRequirements[routeKey]
      if (routeRequirements && routeRequirements.isAdmin) {
        return true
      }
    }

    return false
  }

  private routeRequiresOwner = (route: string): boolean => {
    // Check if the route has any requirements
    const routeKey = Object.keys(Routes).find(key => route.startsWith(Routes[key]))
    if (routeKey) {
      const routeRequirements = RouteRequirements[routeKey]
      if (routeRequirements && routeRequirements.isOwner) {
        return true
      }
    }

    return false
  }
}

export default ControlTower